import React, { useState, useRef, useEffect, useCallback, useContext, useMemo } from 'react'
import {
  Button,
  Badge,
  Overlay,
  Popover,
  Form,
  InputGroup,
  FormControl,
  ButtonGroup
} from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChevronDown,
  faChevronRight,
  faSearch
} from '@fortawesome/free-solid-svg-icons'
import { useAccountCodes } from 'api/hooks/use-account-codes'
import { useDebounce } from 'api/hooks/use-debounce'
import { snakeCase } from 'lodash'
import UserContext from 'context/user-context'

// Map account types from snake_case (backend) to camelCase (frontend params)
const ACCOUNT_TYPE_MAP = {
  org: 'orgs',
  object: 'objects',
  project: 'projects',
  seg_one: 'segOnes',
  seg_two: 'segTwos',
  seg_three: 'segThrees',
  seg_four: 'segFours',
  seg_five: 'segFives',
  seg_six: 'segSixes',
  seg_seven: 'segSevens',
  seg_eight: 'segEights'
}

// Human-readable labels for account types
const ACCOUNT_TYPE_LABELS = {
  org: 'Organization',
  object: 'Object',
  project: 'Project',
  seg_one: 'Segment 1',
  seg_two: 'Segment 2',
  seg_three: 'Segment 3',
  seg_four: 'Segment 4',
  seg_five: 'Segment 5',
  seg_six: 'Segment 6',
  seg_seven: 'Segment 7',
  seg_eight: 'Segment 8'
}

/**
 * AccountCodeFilter - A component for filtering by a specific account code type
 * Displays in an accordion-style UI similar to VendorSearch
 */
const AccountCodeFilter = ({
  accountType,
  params,
  setParams,
  showAsAccordion = true,
  isRequired = false,
  label = null,
  initialOperator = 'include'
}) => {
  const { municipality } = useContext(UserContext)
  const [isExpanded, setIsExpanded] = useState(isRequired)
  const [showPopover, setShowPopover] = useState(false)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [allData, setAllData] = useState([])
  const [selectedItemsData, setSelectedItemsData] = useState([])
  const debouncedSearch = useDebounce(search, 500)
  const lastProcessedSearch = useRef(null)
  const target = useRef(null)
  const popoverRef = useRef(null)

  // Get the segment name from municipality settings if applicable
  const getLabel = () => {
    if (label) return label

    const segmentNameMap = {
      seg_one: municipality?.setting?.seg_one_name,
      seg_two: municipality?.setting?.seg_two_name,
      seg_three: municipality?.setting?.seg_three_name,
      seg_four: municipality?.setting?.seg_four_name,
      seg_five: municipality?.setting?.seg_five_name,
      seg_six: municipality?.setting?.seg_six_name,
      seg_seven: municipality?.setting?.seg_seven_name,
      seg_eight: municipality?.setting?.seg_eight_name
    }

    return segmentNameMap[accountType] || ACCOUNT_TYPE_LABELS[accountType] || accountType
  }

  const codeType = ACCOUNT_TYPE_MAP[accountType]
  const operatorKey = codeType + 'Operator'

  // Initialize operator from template if not already set
  useEffect(() => {
    if (params[operatorKey] === undefined && initialOperator) {
      setParams((prev) => ({
        ...prev,
        [operatorKey]: initialOperator
      }))
    }
  }, [params, operatorKey, initialOperator, setParams])

  const selectedIds = useMemo(() => params[codeType] || [], [params, codeType])
  const isFiltered = selectedIds.length > 0
  const currentOperator = params[operatorKey] || 'include'

  const { data, isSuccess, isFetching } = useAccountCodes({
    params: {
      code_type: codeType,
      page: page,
      perPage: 20,
      [`${snakeCase(codeType)}_search`]: debouncedSearch
    },
    options: {
      enabled: showPopover
    }
  })

  // Handle data accumulation for pagination
  useEffect(() => {
    if (isSuccess && data) {
      const newData = data.data || data

      // Check if search changed - if so, replace all data instead of accumulating
      const searchChanged = lastProcessedSearch.current !== debouncedSearch

      if (page === 1 || searchChanged) {
        setAllData(newData)
      } else {
        setAllData((prev) => {
          const existingIds = new Set(prev.map((item) => item.id))
          const uniqueNewData = newData.filter((item) => !existingIds.has(item.id))
          return [...prev, ...uniqueNewData]
        })
      }

      // Track which search this data is for
      lastProcessedSearch.current = debouncedSearch

      // Update selected items data cache
      const newSelectedItems = newData.filter((item) => selectedIds.includes(item.id))
      if (newSelectedItems.length > 0) {
        setSelectedItemsData((prev) => {
          const updated = [...prev]
          newSelectedItems.forEach((realItem) => {
            const existingIndex = updated.findIndex((item) => item.id === realItem.id)
            if (existingIndex >= 0) {
              updated[existingIndex] = realItem
            } else {
              updated.push(realItem)
            }
          })
          return updated
        })
      }
    }
  }, [data, isSuccess, page, selectedIds, debouncedSearch])

  // Reset pagination when popover opens
  // Note: We do NOT reset selectedItemsData or allData to preserve state across popover open/close
  // The data will be refreshed by the query when the popover opens
  useEffect(() => {
    if (showPopover) {
      setPage(1)
    }
  }, [showPopover])

  // Handle search changes - reset pagination
  useEffect(() => {
    setPage(1)
  }, [debouncedSearch])

  // Handle clicking outside to close popover
  useEffect(() => {
    if (!showPopover) return

    const handleClickOutside = (event) => {
      const popoverElement = popoverRef.current
      const targetElement = target.current

      if (
        popoverElement &&
        !popoverElement.contains(event.target) &&
        targetElement &&
        !targetElement.contains(event.target)
      ) {
        setShowPopover(false)
      }
    }

    // Use mousedown instead of click to catch the event before focus changes
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showPopover])


  const handleCheckboxChange = (accountCode, checked) => {
    if (checked) {
      setSelectedItemsData((prev) => {
        const existingIds = prev.map((item) => item.id)
        if (!existingIds.includes(accountCode.id)) {
          return [...prev, accountCode]
        }
        return prev
      })

      setParams((prev) => ({
        ...prev,
        [codeType]: prev[codeType] ? [...prev[codeType], accountCode.id] : [accountCode.id]
      }))
    } else {
      setSelectedItemsData((prev) => prev.filter((item) => item.id !== accountCode.id))

      setParams((prev) => ({
        ...prev,
        [codeType]: (prev[codeType] || []).filter((id) => id !== accountCode.id)
      }))
    }
  }

  const handleOperatorToggle = () => {
    setParams((prev) => ({
      ...prev,
      [operatorKey]: prev[operatorKey] === 'include' ? 'exclude' : 'include'
    }))
  }

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target
    if (scrollHeight - scrollTop <= clientHeight + 50 && !isFetching && data) {
      const dataArray = data.data || data
      if (dataArray.length > 0) {
        setPage((prev) => prev + 1)
      }
    }
  }

  const getOrganizedData = useCallback(() => {
    const selected = selectedItemsData.filter((item) => selectedIds.includes(item.id))

    const uniqueUnselected = new Map()
    allData.forEach((item) => {
      if (!selectedIds.includes(item.id)) {
        uniqueUnselected.set(item.id, item)
      }
    })
    let unselected = Array.from(uniqueUnselected.values())

    if (debouncedSearch && debouncedSearch.trim()) {
      const searchTerm = debouncedSearch.toLowerCase()
      unselected = unselected.filter(
        (item) =>
          item.name?.toLowerCase().includes(searchTerm) ||
          item.code?.toLowerCase().includes(searchTerm) ||
          item.external_id?.toLowerCase().includes(searchTerm)
      )
    }

    return { selected, unselected }
  }, [selectedItemsData, selectedIds, allData, debouncedSearch])

  const formatValue = () => {
    if (!isFiltered) {
      return 'Not set'
    }
    const operatorText = currentOperator === 'exclude' ? 'Excludes' : 'Contains'
    return `${operatorText} ${selectedIds.length} ${selectedIds.length === 1 ? 'code' : 'codes'}`
  }

  const renderPopover = () => (
    <Overlay
      target={target.current}
      show={showPopover}
      placement="bottom-start"
    >
      <Popover
        id={`popover-account-${accountType}`}
        style={{
          maxHeight: 400,
          minWidth: '350px',
          overflowY: 'auto'
        }}
        className="shadow-sm"
      >
        <div ref={popoverRef}>
          <Popover.Body className="bg-light">
            <div className="d-flex gap-2 align-items-center">
              <InputGroup size="sm" className="flex-grow-1">
                <InputGroup.Text>
                  <FontAwesomeIcon icon={faSearch} />
                </InputGroup.Text>
                <FormControl
                  placeholder="Search by ID or Name"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  autoFocus
                />
              </InputGroup>
            <ButtonGroup size="sm">
              <Button
                variant={currentOperator === 'include' ? 'primary' : 'outline-primary'}
                onClick={handleOperatorToggle}
              >
                Include
              </Button>
              <Button
                variant={currentOperator === 'exclude' ? 'primary' : 'outline-primary'}
                onClick={handleOperatorToggle}
              >
                Exclude
              </Button>
            </ButtonGroup>
            </div>
          </Popover.Body>
          <hr className="m-0" />
          <Popover.Body style={{ maxHeight: '250px', overflowY: 'auto' }} onScroll={handleScroll}>
          {(() => {
            const { selected, unselected } = getOrganizedData()

            return (
              <>
                {selected.length > 0 && (
                  <>
                    <div className="mb-2">
                      <small className="text-muted fw-bold">
                        {currentOperator.toUpperCase()}D CODES ({selected.length})
                      </small>
                    </div>
                    {selected.map((accountCode) => (
                      <Form.Check
                        key={`selected-${accountCode.id}`}
                        type="checkbox"
                        id={`filter-${accountType}-selected-${accountCode.id}`}
                        className="mb-1"
                        label={
                          <>
                            <span>{accountCode.name}</span>{' '}
                            <span className="text-muted">{accountCode.code}</span>
                          </>
                        }
                        checked={true}
                        onChange={(e) => handleCheckboxChange(accountCode, e.target.checked)}
                      />
                    ))}
                    <hr className="my-2" />
                  </>
                )}
                <div>
                  {unselected.length === 0 && !isFetching && isSuccess && (
                    <div className="text-center text-muted small">No accounts found</div>
                  )}

                  {unselected.map((accountCode) => (
                    <Form.Check
                      key={accountCode.id}
                      type="checkbox"
                      id={`filter-${accountType}-${accountCode.id}`}
                      className="mb-1"
                      label={
                        <>
                          <span>{accountCode.name}</span>{' '}
                          <span className="text-muted">{accountCode.code}</span>
                        </>
                      }
                      checked={false}
                      onChange={(e) => handleCheckboxChange(accountCode, e.target.checked)}
                    />
                  ))}

                  {(isFetching || (!isSuccess && showPopover)) && (
                    <div className="text-center py-2">
                      <small className="text-muted">Loading...</small>
                    </div>
                  )}
                </div>
              </>
            )
          })()}
          </Popover.Body>
        </div>
      </Popover>
    </Overlay>
  )

  if (!showAsAccordion) {
    return (
      <>
        <Button
          ref={target}
          variant={isFiltered ? 'outline-primary' : 'light-primary'}
          size="sm"
          onClick={() => setShowPopover(!showPopover)}
        >
          {getLabel()}
          {isFiltered && <Badge bg="primary" className="ms-1">{selectedIds.length}</Badge>}
        </Button>
        {renderPopover()}
      </>
    )
  }

  return (
    <div className="mb-2">
      <div
        className={`d-flex justify-content-between align-items-center p-3 border rounded ${
          isRequired && !isFiltered ? 'border-warning' : ''
        }`}
        style={{
          cursor: 'pointer',
          backgroundColor: isExpanded ? '#f8f9fa' : 'white'
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="d-flex align-items-center gap-3 flex-grow-1">
          <div className="d-flex align-items-center gap-2">
            <strong>{getLabel()}</strong>
            {isRequired && !isFiltered && (
              <Badge bg="warning" text="dark" className="small">
                Required
              </Badge>
            )}
          </div>
          {!isExpanded && <div className="text-muted small">{formatValue()}</div>}
        </div>
        <FontAwesomeIcon
          icon={isExpanded ? faChevronDown : faChevronRight}
          className="text-muted"
        />
      </div>

      {isExpanded && (
        <div className="p-3 border border-top-0 rounded-bottom" onClick={(e) => e.stopPropagation()}>
          <Button
            ref={target}
            variant={isFiltered ? 'outline-primary' : 'light'}
            size="sm"
            onClick={() => setShowPopover(!showPopover)}
          >
            {isFiltered ? formatValue() : `Select ${getLabel()}`}
          </Button>
          {renderPopover()}
        </div>
      )}
    </div>
  )
}

export default AccountCodeFilter
